import { Button } from "@components/ui/button";
import useDialog from "@hooks/useDialog";
import { useMusicControl } from "@hooks/useMusicControl";
import { useScreens } from "@hooks/useScreens";
import DialogComponent from "./shared/Dialog";

interface ManageControlDialogProps {
  screenId: string;
  locationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManageControlDialog({
  screenId,
  locationId,
  open,
  onOpenChange,
}: ManageControlDialogProps) {
  const { screens: _screens } = useScreens();
  const { controlInfo, controlInfos } = useMusicControl(screenId, locationId);
  const { approveControl, rejectControl, revokeControl } = useMusicControl(screenId, locationId);
  const { showSuccessSnackbar, showErrorSnackbar } = useDialog()

  // Only show screens that have requested control
  const requests = controlInfos?.filter((c) => c?.request_status === "pending");

  const handleApprove = async (targetScreenId: string) => {
    try {
      await approveControl.mutateAsync(targetScreenId);
      showSuccessSnackbar("Control request approved");
      onOpenChange(false);
    } catch (error) {
      showErrorSnackbar("Failed to approve control request");
    }
  };

  const handleReject = async (targetScreenId: string) => {
    try {
      await rejectControl.mutateAsync(targetScreenId);
      showSuccessSnackbar("Control request rejected");
      onOpenChange(false);
    } catch (error) {
      showErrorSnackbar("Failed to reject control request");
    }
  };

  const handleRevoke = async (targetScreenId: string) => {
    try {
      await revokeControl.mutateAsync(targetScreenId);
      showSuccessSnackbar("Control access revoked");
      onOpenChange(false);
    } catch (error) {
      showErrorSnackbar("Failed to revoke control access");
    }
  };

  if (!controlInfo?.is_owner) {
    return null;
  }

  return <DialogComponent
    closeDialog={() => onOpenChange(false)}
    isDialogOpen={open}
    dialogInfo={{
      title: "Manage Music Control",
      description: "Manage control requests and permissions for other screens in this location.",
      customContent: (
        <div className="space-y-4">
          {requests?.length === 0 ? (
            <p className="text-center text-sm text-gray-500">No pending control requests</p>
          ) : (
            requests?.map((req) => {
              const screen = _screens.find((s) => s.id === req.screen_id);
              if (!screen) {
                return null;
              }
              return <div key={screen.id} className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <h4 className="font-medium">{screen.name}</h4>
                  <p className="text-sm text-gray-500">
                    Requesting control
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleApprove(screen.id)}
                    disabled={approveControl.isPending}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleReject(screen.id)}
                    disabled={rejectControl.isPending}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            })
          )}
        </div>
      )
    }}
  />
}